/**
 * Trunnion AI - Vertical Timeline Component
 */

import type { ReactNode } from 'react';

interface TimelineItem {
  id: string;
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  timestamp?: string;
  content?: ReactNode;
  status?: 'completed' | 'active' | 'pending' | 'error';
}

interface TimelineProps {
  items: TimelineItem[];
  className?: string;
}

const statusColors: Record<string, { dot: string; line: string }> = {
  completed: { dot: 'bg-green-400 border-green-400', line: 'bg-green-400/30' },
  active: { dot: 'bg-blue-400 border-blue-400', line: 'bg-blue-400/30' },
  pending: { dot: 'bg-trunnion-slate-500 border-trunnion-slate-500', line: 'bg-trunnion-slate-700' },
  error: { dot: 'bg-red-400 border-red-400', line: 'bg-red-400/30' },
};

export function Timeline({ items, className = '' }: TimelineProps) {
  return (
    <div className={`relative ${className}`}>
      {items.map((item, index) => {
        const status = item.status ?? 'pending';
        const colors = statusColors[status];
        const isLast = index === items.length - 1;

        return (
          <div key={item.id} className="relative flex gap-4 pb-6 last:pb-0">
            {/* Line */}
            {!isLast && (
              <div
                className={`absolute left-[11px] top-6 h-[calc(100%-12px)] w-0.5 ${colors.line}`}
              />
            )}

            {/* Dot */}
            <div className="relative shrink-0 mt-1">
              {item.icon ? (
                <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-trunnion-slate-700 bg-trunnion-slate-800">
                  {item.icon}
                </div>
              ) : (
                <div
                  className={`h-6 w-6 rounded-full border-2 ${colors.dot} ${
                    status === 'active' ? 'animate-pulse' : ''
                  }`}
                >
                  {status === 'completed' && (
                    <svg
                      className="h-full w-full text-trunnion-slate-900 p-0.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-white">{item.title}</h4>
                {item.timestamp && (
                  <time className="text-xs text-trunnion-slate-400">
                    {item.timestamp}
                  </time>
                )}
              </div>
              {item.subtitle && (
                <p className="mt-0.5 text-xs text-trunnion-slate-400">
                  {item.subtitle}
                </p>
              )}
              {item.content && <div className="mt-2">{item.content}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
